import { Form, FormApi, Icon, TextBox, reactive } from 'cdui-js';
import { aesEncryptHexByPass } from '../../../../../lib/crypto';
import { saveUserInfo } from '../../../../../api/open-account';
import { initBusConfigs } from '../../../../../stores/bus-config';
import { commonState } from '../../../../../stores/common';
import { CheckBox } from '../../../components/CheckBox';
import { DatePicker } from '../../../components/DatePicker';
import { SelectBox } from '../../../components/SelectBox';
import { SelectCountry } from '../../../components/SelectCountry';
import { showHud, showToast } from '../../../components/Toast';
import { OpenFormItem } from '../../open-progress/components/OpenFormItem';
import { UploadIdentity } from '../../open-progress/UploadIdentity';
import { lastVirtualStep, nextVirtualStep, virtualApplyState } from './store';
import { DiFiInput } from '../../../../../components/DiFi/DiFiInput';
import i18n from '../../../../../i18n';
import { addAccountBarPadBottom, addAccountContentReserve } from '../safeAreaBar';
import { applyJoinedNames, fillOcrNameParts, hasRequiredName } from '../../nameParts';
import {
  createIdCardEndDateRules,
  getIdCardEndDateError,
  syncIdCardDateRange,
} from '../../open-progress/on-line/infomations/idCardValidityDates';

const getTitleMap = (): Record<string, string> => ({
  HONG_KONG: i18n.AddAccount.UploadHkId,
  MACAO: i18n.AddAccount.UploadMoId,
  OTHER_REGION: i18n.AddAccount.UploadOtherId,
});

const getExpListMap = (): Record<string, { img: string; text: string }[]> => {
  const A = i18n.AddAccount;
  return {
    HONG_KONG: [
      { img: '/images/open-exp1-hk.png', text: A.ExpHk1 },
      { img: '/images/open-exp2-hk.png', text: A.ExpHk2 },
      { img: '/images/open-exp3-hk.png', text: A.ExpGlare },
      { img: '/images/open-exp4-hk.png', text: A.ExpCorner },
    ],
    MACAO: [
      { img: '/images/open-exp1-mo.png', text: A.ExpMo1 },
      { img: '/images/open-exp2-mo.png', text: A.ExpMo2 },
      { img: '/images/open-exp3-mo.png', text: A.ExpGlare },
      { img: '/images/open-exp4-mo.png', text: A.ExpCorner },
    ],
    OTHER_REGION: [
      { img: '/images/open-exp1-other.png', text: A.ExpOther1 },
      { img: '/images/open-exp2-other.png', text: A.ExpGlare },
      { img: '/images/open-exp3-other.png', text: A.ExpOther2 },
      { img: '/images/open-exp4-other.png', text: A.ExpOther3 },
    ],
  };
};

const UploadOverseasLic = () => {
  let formApi: FormApi = null;
  const state = reactive({ loading: false });
  const form = virtualApplyState.uploadLicForm;

  initBusConfigs();

  const idType = () => virtualApplyState.overseasIdentityType || 'HONG_KONG';
  const expList = () => getExpListMap()[idType()] || getExpListMap().HONG_KONG;

  return (
    <div>
      <div class="padding-x-L padding-y" style={{ 'margin-bottom': `${addAccountContentReserve(140)}px` }}>
        <div class="font-s-L font-bold color-D margin-b-XS">{i18n.AddAccount.UploadTitle}</div>
        <div class="color-D font-s margin-b-XS">{getTitleMap()[idType()]}</div>
        <div class="margin-b-L font-s-S color-L">{i18n.AddAccount.UploadTip}</div>

        <div class="color-error margin-b-XS">{i18n.AddAccount.UploadRejectHint}</div>
        <div class="flex flex-wrap gap-12px margin-b-L">
          {expList().map((item) => (
            <div style={{ width: '48%' }}>
              <div
                class="bg-c-XD round-XS"
                style={{ 'aspect-ratio': '182 / 110', overflow: 'hidden' }}
              >
                <img
                  src={item.img}
                  width={182}
                  height={110}
                  decoding="async"
                  draggable={false}
                  class="width-100"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    'object-fit': 'cover',
                    'pointer-events': 'none',
                  }}
                />
              </div>
              <div class="flex align-center gap-4px color-D">
                <Icon name="common-close" class="icon-c-primary icon-s-L icon-s-XXS" />
                <span class="font-s-XXS">{item.text}</span>
              </div>
            </div>
          ))}
        </div>

        <UploadIdentity
          type="identity_front"
          isOCR={false}
          img={virtualApplyState.identityFrontUrl}
          busId={virtualApplyState.busId}
          change={({ url, data }) => {
            virtualApplyState.identityFrontUrl = url || '';
            if (data?.ocrResult) {
              fillOcrNameParts(form, data.ocrResult);
              form.idCardNo = data.ocrResult.idNumber || form.idCardNo;
            }
          }}
        />

        <UploadIdentity
          type="identity_back"
          isOCR={false}
          class="margin-t-L"
          img={virtualApplyState.identityBackUrl}
          busId={virtualApplyState.busId}
          change={({ url, data }) => {
            virtualApplyState.identityBackUrl = url || '';
            if (data?.ocrResult) {
              const dates = (data.ocrResult?.validPeriod || '').split('-');
              form.idCardIssueAuthority = data.ocrResult.issueAuthority || form.idCardIssueAuthority;
              if (dates.length) {
                form.idCardStartDate = dates[0] || form.idCardStartDate;
                form.idCardEndDate = dates[1] || form.idCardEndDate;
                if (dates[0]) {
                  form.idCardLongTerm = 0;
                }
                syncIdCardDateRange(form);
              }
            }
          }}
        />

        <div class="margin-t-L">
          <div class="color-D font-s font-bold">{i18n.AddAccount.IdInfo}</div>

          <Form
            data={form}
            rules={createIdCardEndDateRules(
              () => form.idCardStartDate,
              () => form.idCardLongTerm,
            )}
            align="top"
            api={(api) => {
              formApi = api;
            }}
          >
            <OpenFormItem label={i18n.OpenProgress.Info.UploadLic.LabelFullName} field="fullName" class="margin-t-L">
              <DiFiInput
                shellClass="f-input-2"
                inputKind="text"
                maxLength={50}
                placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
                value={form.fullName}
                onUpdateValue={(v) => {
                  form.fullName = v;
                }}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.OpenProgress.Info.UploadLic.LabelFirstNameEn} field="firstNameEn" class="margin-t-L">
              <DiFiInput
                shellClass="f-input-2"
                inputKind="english"
                maxLength={50}
                placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
                value={form.firstNameEn}
                onUpdateValue={(v) => {
                  form.firstNameEn = v;
                  applyJoinedNames(form);
                }}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.OpenProgress.Info.UploadLic.LabelLastNameEn} field="lastNameEn" class="margin-t-L">
              <DiFiInput
                shellClass="f-input-2"
                inputKind="english"
                maxLength={50}
                placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
                value={form.lastNameEn}
                onUpdateValue={(v) => {
                  form.lastNameEn = v;
                  applyJoinedNames(form);
                }}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.AddAccount.IdCardNo} field="idCardNo" required class="margin-t-L">
              <DiFiInput
                shellClass="f-input-2"
                inputKind="text"
                maxLength={50}
                placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
                value={form.idCardNo}
                onUpdateValue={(v) => (form.idCardNo = v)}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.AddAccount.IssueAuthority} field="idCardIssueAuthority" class="margin-t-L">
              <DiFiInput
                shellClass="f-input-2"
                inputKind="text"
                maxLength={50}
                placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
                value={form.idCardIssueAuthority}
                onUpdateValue={(v) => (form.idCardIssueAuthority = v)}
              />
            </OpenFormItem>

            {form.idCardLongTerm !== 1 && (
              <div class="flex margin-t-L gap-16px">
                <OpenFormItem label={i18n.AddAccount.ValidStart} field="idCardStartDate" required class="flex-auto">
                  <DatePicker
                    value={form.idCardStartDate}
                    placeholder={i18n.OpenProgress.Common.PlaceholderSelect}
                    title={i18n.AddAccount.ValidStart}
                    maxYear={2099}
                    change={(v) => {
                      form.idCardStartDate = v;
                      form.idCardLongTerm = 0;
                      syncIdCardDateRange(form);
                    }}
                  />
                </OpenFormItem>
                <OpenFormItem label={i18n.AddAccount.ValidEnd} field="idCardEndDate" required class="flex-auto">
                  <DatePicker
                    value={form.idCardEndDate}
                    placeholder={i18n.OpenProgress.Common.PlaceholderSelect}
                    title={i18n.AddAccount.ValidEnd}
                    maxYear={2099}
                    change={(v) => {
                      const err = getIdCardEndDateError(form.idCardStartDate, v, 0);
                      if (err) {
                        showHud(err);
                        return;
                      }
                      form.idCardEndDate = v;
                      form.idCardLongTerm = 0;
                    }}
                  />
                </OpenFormItem>
              </div>
            )}

            <CheckBox
              active={form.idCardLongTerm === 1}
              change={(e) => {
                form.idCardLongTerm = e ? 1 : 0;
                if (e) {
                  form.idCardStartDate = '';
                  form.idCardEndDate = '';
                }
              }}
            >
              <span class="color-D">{i18n.AddAccount.LongTermValid}</span>
            </CheckBox>

            <OpenFormItem label={i18n.AddAccount.BirthDate} field="birthDate" required class="margin-t-L">
              <DatePicker
                value={form.birthDate}
                placeholder={i18n.OpenProgress.Common.PlaceholderBirthDate}
                change={(v) => (form.birthDate = v)}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.AddAccount.Gender} field="gender" required class="margin-t-L">
              <SelectBox
                value={form.gender}
                title={i18n.AddAccount.Gender}
                dict="gender"
                change={(e) => (form.gender = e)}
              />
            </OpenFormItem>

            <OpenFormItem label={i18n.AddAccount.BirthCountry} field="birthCountry" required class="margin-t-L">
              <SelectCountry value={form.birthCountry} change={(e) => (form.birthCountry = e)} />
            </OpenFormItem>
          </Form>
        </div>
      </div>

      <div
        class="fixed position-b position-l width-100 bg-c padding-x-L flex gap-8px"
        style={{
          'padding-top': '12px',
          'padding-bottom': `${addAccountBarPadBottom(16)}px`,
          'z-index': 150,
        }}
      >
        <button
          class="default-btn btn-rounded"
          style={{ flex: 1 }}
          onclick={() => lastVirtualStep()}
          disabled={state.loading}
        >
          {i18n.OpenProgress.Common.Prev}
        </button>
        <button
          class="confirm-btn btn-rounded"
          style={{ flex: 2 }}
          onclick={async () => {
            if (state.loading) return;
            if (!virtualApplyState.identityFrontUrl) {
              showToast(i18n.AddAccount.ToastUploadFront);
              return;
            }
            if (!virtualApplyState.identityBackUrl) {
              showToast(i18n.AddAccount.ToastUploadBack);
              return;
            }
            if (!hasRequiredName(form)) {
              showToast(i18n.OpenProgress.Info.UploadLic.ToastNameRequired);
              return;
            }
            applyJoinedNames(form);
            const valid = await formApi?.validate();
            if (!valid) return;
            // 编辑/回填旧到期日：cdui required 只验非空、不跑 onvalidate；未重选日期时须显式拦
            const endErr = getIdCardEndDateError(
              form.idCardStartDate,
              form.idCardEndDate,
              form.idCardLongTerm,
            );
            if (endErr) {
              showHud(endErr);
              return;
            }

            state.loading = true;
            try {
              const data = JSON.parse(JSON.stringify(form));
              data.idCardNo = aesEncryptHexByPass(data.idCardNo, commonState.encryptKey);
              // 仅落境外证件明细，不改 identityType，避免覆盖大陆居民身份
              await saveUserInfo({
                ...data,
                idCardType: idType(),
              });
              nextVirtualStep();
            } catch (e) {
              showToast(e);
            } finally {
              state.loading = false;
            }
          }}
          disabled={state.loading}
        >
          {state.loading ? i18n.AddAccount.Submitting : i18n.OpenProgress.Common.Next}
        </button>
      </div>
    </div>
  );
};

export default UploadOverseasLic;
